import { UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Col, message, Row, Typography, Upload } from "antd";
import React, { useState } from "react";
import { authAxios } from "../utils/session_utils";

const { Title } = Typography;

const HorizonPdfUploader: React.FC = () => {
	const [fileList, setFileList] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(false);

	const handleBeforeUpload = (file: File) => {
		const isPDF = file.type === "application/pdf";
		if (!isPDF) {
			message.error("Only PDF files are allowed!");
			return Upload.LIST_IGNORE;
		}

		setFileList([
			{
				originFileObj: file,
				name: file.name,
				status: "done",
			},
		]);

		return false;
	};

	const handleOnChange = (info: any) => {
		setFileList(info.fileList);
	};

	const sanitizeCellValue = (value: string): string => {
		if (/^-.+[A-Za-z]$/.test(value.trim())) {
			// starts with dash, ends with letter
			return `'${value}`;
		}
		return value;
	};

	const handleUpload = async () => {
		if (!fileList.length) {
			message.error("No file selected");
			return;
		}

		setLoading(true);
		const hideLoading = message.loading("Uploading PDF...", 0);

		try {
			// Create a FormData object to send the file
			const formData = new FormData();
			formData.append("file", fileList[0].originFileObj);

			// Use authAxios instead of fetch
			const config = {
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/salesforce/upload_horizon_pdf`,
				data: formData,
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const response = await authAxios(config);

			if (!response?.data?.success) {
				throw new Error(response?.data?.error || "Failed to upload PDF.");
			}

			if (response.data.payload) {
				const payload = JSON.parse(response.data.payload);
				if (!payload.body) {
					throw new Error("No CSV content found in response.");
				}
				const processedCsvContent = payload.body.split("\n").map((row: string) =>
						row.split(",").map(cell => sanitizeCellValue(cell)).join(",")
					).join("\n");
				const csvContent = processedCsvContent;
				const filename = fileList[0]?.name
					? `${fileList[0].name.replace(/\.pdf$/i, "")}.csv`
					: "downloaded_file.csv";
				const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.setAttribute("download", filename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);

				message.success("PDF uploaded and CSV downloaded successfully!");
				setFileList([]);
			} else {
				message.success("PDF uploaded successfully (no CSV data found).");
			}

			setLoading(false);
			hideLoading();
		} catch (error: any) {
			console.error("Upload error:", error);
			setLoading(false);
			hideLoading();
			message.error(`Upload failed: ${error.message || "Unknown error"}`);
		}
	};

	return (
		<>
			<Card
				title={<Title level={4}>Horizon PDF Uploader</Title>}
				style={{ maxWidth: 900, margin: "20px auto", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)" }}
				bordered={true}
			>
				<Row gutter={[16, 16]}>
					<Col span={24}>
						<Upload
							beforeUpload={handleBeforeUpload}
							onChange={handleOnChange}
							fileList={fileList}
							maxCount={1}
							onRemove={() => setFileList([])}
						>
							<Button icon={<UploadOutlined />} disabled={loading} size="large">
								Select PDF
							</Button>
						</Upload>
					</Col>
				</Row>
				{fileList.length > 0 && (
					<Row style={{ marginTop: 16 }}>
						<Col span={24}>
							<Alert
								message="File Selected"
								description={`File "${fileList[0].name}" is ready to upload.`}
								type="info"
								showIcon
							/>
						</Col>
					</Row>
				)}

				<Row gutter={[16, 16]} style={{ marginTop: 16 }}>
					<Col span={24}>
						<Button
							type="primary"
							onClick={handleUpload}
							disabled={!fileList.length || loading}
							loading={loading}
							size="large"
							block
						>
							{loading ? "Uploading..." : "Upload PDF"}
						</Button>
					</Col>
				</Row>
			</Card>
		</>
	);
};

export default HorizonPdfUploader;
